import type { SupabaseClient } from '@supabase/supabase-js';
import { useEffect, useRef, useState } from 'react';
import { enrolTotp, verifyCode } from '../auth/mfa';

interface Props {
  sb: SupabaseClient;
  onDone: () => void;
}

// 04 ARC-187: the same answer whether or not an account exists.
export const SIGN_IN_FAILED = 'That did not work. Check the email and password, and try again.';

export function SignInPage({ sb, onDone }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState('');
  return (
    <main>
      <h1>Sign in</h1>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const { error } = await sb.auth.signInWithPassword({ email, password });
          if (error) setMsg(SIGN_IN_FAILED);
          else onDone();
        }}
      >
        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <p>
          <button className="primary" type="submit">
            Sign in
          </button>
        </p>
        <p role="status">{msg}</p>
      </form>
    </main>
  );
}

export function EnrolPage({ sb, onDone }: Props) {
  const [qr, setQr] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | undefined>();
  const [msg, setMsg] = useState('');
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return; // one enrolment, even when React runs effects twice
    started.current = true;
    enrolTotp(sb)
      .then((d) => {
        setFactorId(d.id);
        setQr(d.totp.qr_code);
      })
      .catch(() => setMsg('The picture did not load. Refresh the page to try again.'));
  }, [sb]);
  return (
    <main>
      <h1>Set up your second step</h1>
      <p>Open your authenticator app. Scan this picture. Then type the 6-digit code.</p>
      {qr && (
        <img src={qr} alt="A code to scan with your authenticator app" width={220} height={220} />
      )}
      <p role="status">{msg}</p>
      <CodeForm sb={sb} onDone={onDone} factorId={factorId} />
    </main>
  );
}

export function CodePage({ sb, onDone }: Props) {
  return (
    <main>
      <h1>Your code</h1>
      <p>Open your authenticator app. Type the 6-digit code for Fairway Life.</p>
      <CodeForm sb={sb} onDone={onDone} />
    </main>
  );
}

export function NotAdminPage({ sb, onDone }: Props) {
  return (
    <main>
      <h1>Not an admin account</h1>
      <p>
        This account cannot use the admin console. Sign out, then sign in with your admin account.
      </p>
      <p>
        <button className="primary" onClick={() => void sb.auth.signOut().then(onDone)}>
          Sign out
        </button>
      </p>
    </main>
  );
}

function CodeForm({ sb, onDone, factorId }: Props & { factorId?: string }) {
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (await verifyCode(sb, code.trim(), factorId)) onDone();
        else setMsg('That code did not work. Wait for a new code, then try again.');
      }}
    >
      <label htmlFor="code">6-digit code</label>
      <input
        id="code"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        required
      />
      <p>
        <button className="primary" type="submit">
          Continue
        </button>
      </p>
      <p role="status">{msg}</p>
    </form>
  );
}
